import { useEffect, useRef, useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { roleActionErrorToast, roleFormSuccessToast } from '@/lib/user-feedback';
import { isSystemRole, hierarchyLevelLabel, roleHierarchyLevel, roleLabel } from '@/lib/role-display';
import { DEFAULT_CUSTOM_ROLE_LEVEL, MIN_ROLE_HIERARCHY_LEVEL } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { RolePermissionMatrix } from './RolePermissionMatrix';

const HIERARCHY_MESSAGE = `El nivel jerárquico debe ser un entero mayor o igual a ${MIN_ROLE_HIERARCHY_LEVEL}.`;
const CANNOT_EDIT_ROLE = 'No puede editar este rol: su nivel jerárquico no es superior al del rol seleccionado.';

interface RoleForm {
  name: string;
  hierarchyLevel: number;
}

interface RoleDetail {
  id: string;
  name: string;
  label?: string | null;
  hierarchyLevel?: number;
  hierarchyLevelStored?: number | null;
  permissions?: string[];
}

export function RoleFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { canManageRole } = usePermissions();
  const isEdit = Boolean(id);
  const [permissions, setPermissions] = useState<string[]>([]);
  const cannotEditToastFor = useRef<string | null>(null);

  const { data: existing, isLoading } = useQuery({
    queryKey: ['roles', id],
    queryFn: () => api<RoleDetail>(`/roles/${id}`),
    enabled: isEdit,
  });

  const systemRole = existing ? isSystemRole(existing.name) : false;
  const displayLabel = existing ? roleLabel(existing.name, existing.label) : '';
  const canEditRole = !isEdit || (existing ? canManageRole(existing.name, existing.hierarchyLevelStored) : true);

  const { register, handleSubmit, watch } = useForm<RoleForm>({
    values: existing
      ? {
          name: existing.name,
          hierarchyLevel: existing.hierarchyLevel ?? roleHierarchyLevel(existing.name),
        }
      : undefined,
    defaultValues: { name: '', hierarchyLevel: DEFAULT_CUSTOM_ROLE_LEVEL },
  });

  const hierarchyLevel = watch('hierarchyLevel');

  useEffect(() => {
    if (existing?.permissions) {
      setPermissions(existing.permissions);
    }
  }, [existing?.permissions]);

  useEffect(() => {
    if (!isEdit || isLoading || canEditRole || !id) return;
    if (cannotEditToastFor.current === id) return;
    cannotEditToastFor.current = id;
    toast({ title: CANNOT_EDIT_ROLE, variant: 'danger' });
  }, [isEdit, isLoading, canEditRole, id, toast]);

  function toastInvalidForm(errors: FieldErrors<RoleForm>) {
    const message = errors.name?.message || errors.hierarchyLevel?.message;
    if (message) toast({ title: String(message), variant: 'danger' });
  }

  function handleCancel() {
    if (existing?.permissions) {
      setPermissions(existing.permissions);
    }
    navigate(isEdit ? `/roles/${id}` : '/roles');
  }

  const mutation = useMutation({
    mutationFn: (values: RoleForm) => {
      const body = {
        name: values.name.trim(),
        hierarchyLevel: Number(values.hierarchyLevel),
        permissions,
      };
      return isEdit
        ? api(`/roles/${id}`, { method: 'PATCH', body })
        : api('/roles', { method: 'POST', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      if (isEdit && id) void queryClient.invalidateQueries({ queryKey: ['roles', id] });
      toast(roleFormSuccessToast(isEdit));
      navigate(isEdit ? `/roles/${id}` : '/roles');
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo guardar el rol'));
    },
  });

  return (
    <div className="lms-permission-matrix mb-24 font-sans">
      {isEdit ? (
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-4">
            <HubBackLink to={`/roles/${id}`} label="Volver al detalle del rol" />
            <div>
              <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Editar rol</h1>
              <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
                {displayLabel} — actualice los permisos asignados al rol.
              </p>
            </div>
          </div>
          <div className="flex w-full flex-col gap-3 md:w-auto md:items-end">
            <div className="flex w-full flex-col rounded-lg bg-[#2e3a8c] px-6 py-3 text-[#9ea9ff] md:w-auto md:items-end">
              <span className="pm-label-sm uppercase tracking-wider opacity-80">Editando permisos de:</span>
              <div className="mt-1 flex items-center gap-2">
                <span className="material-symbols-outlined text-2xl" aria-hidden="true">
                  admin_panel_settings
                </span>
                <span className="pm-headline-md text-[#9ea9ff]">
                  {displayLabel}
                  {systemRole ? ' (Sistema)' : ''}
                </span>
              </div>
              <span className="pm-label-sm mt-1" id="role-pm-total-active-label">
                {permissions.length}{' '}
                {permissions.length === 1 ? 'permiso activo' : 'permisos activos'} en total
                {hierarchyLevel != null ? ` · ${hierarchyLevelLabel(hierarchyLevel)}` : ''}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex items-start gap-4">
          <HubBackLink to="/roles" label="Volver a roles" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Nuevo rol</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Crear un nuevo rol con permisos personalizados para la plataforma.
            </p>
          </div>
        </div>
      )}

      {isEdit && isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
        <form id="role-form" className="space-y-12" onSubmit={handleSubmit((v) => mutation.mutate(v), toastInvalidForm)}>
          <div className="space-y-12">
            <section className="rounded-xl border border-dash-outline-variant bg-white p-8">
              <div className="grid max-w-4xl gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="role-name" className="mb-2 block text-sm font-medium text-dash-on-surface-variant">
                    Nombre del rol
                    {!systemRole && <span className="text-error-600"> *</span>}
                  </label>
                  <input
                    id="role-name"
                    type="text"
                    disabled={systemRole || !canEditRole}
                    placeholder="Ej. Coordinador Académico"
                    className={`h-12 w-full rounded-lg border border-dash-outline-variant bg-dash-surface px-4 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20 ${
                      systemRole || !canEditRole ? 'cursor-not-allowed opacity-80' : ''
                    }`}
                    {...register('name', {
                      validate: (value) =>
                        systemRole || value.trim().length > 0 || 'El nombre del rol es obligatorio',
                    })}
                  />
                  {systemRole && (
                    <p className="mt-2 text-xs text-dash-on-surface-variant">Los roles del sistema no pueden renombrarse.</p>
                  )}
                </div>
                <div>
                  <label htmlFor="role-hierarchy-level" className="mb-2 block text-sm font-medium text-dash-on-surface-variant">
                    Nivel jerárquico
                    <span className="text-error-600"> *</span>
                  </label>
                  <input
                    id="role-hierarchy-level"
                    type="number"
                    step={1}
                    disabled={!canEditRole}
                    placeholder="Ej. 5"
                    className={`h-12 w-full rounded-lg border border-dash-outline-variant bg-dash-surface px-4 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20 ${
                      !canEditRole ? 'cursor-not-allowed opacity-80' : ''
                    }`}
                    {...register('hierarchyLevel', {
                      valueAsNumber: true,
                      validate: (value) => {
                        if (typeof value !== 'number' || Number.isNaN(value)) {
                          return 'El nivel jerárquico es obligatorio';
                        }
                        return (
                          (Number.isInteger(value) && value >= MIN_ROLE_HIERARCHY_LEVEL) || HIERARCHY_MESSAGE
                        );
                      },
                    })}
                  />
                  <p className="mt-2 text-xs text-dash-on-surface-variant">
                    N0 = máxima autoridad. Número menor = más privilegio.
                    {hierarchyLevel != null && !Number.isNaN(hierarchyLevel) ? ` Actual: ${hierarchyLevelLabel(hierarchyLevel)}.` : ''}
                  </p>
                </div>
              </div>
            </section>

            <RolePermissionMatrix selected={permissions} onChange={setPermissions} hideHeader={isEdit} readOnly={!canEditRole} />
          </div>
        </form>

        <div className="lms-permission-matrix-action-bar pm-border fixed bottom-0 left-0 z-40 w-full border-t bg-[#f9f9fc] py-4">
          <div className="lms-site-admin-sticky-bar__inner flex items-center justify-between gap-4">
            <div className="pm-label-md pm-text-variant hidden items-center gap-4 md:flex">
              <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                info
              </span>
              {isEdit ? (
                <>
                  Los cambios afectarán a todos los usuarios con el rol &quot;{displayLabel}&quot;.
                </>
              ) : (
                <>Los permisos seleccionados se aplicarán al rol al guardar.</>
              )}
            </div>
            <div className="flex w-full gap-4 md:w-auto">
              <button
                type="button"
                onClick={handleCancel}
                className="pm-label-md pm-border pm-radius-pill pm-text-variant inline-flex flex-1 items-center justify-center border bg-white px-8 py-2.5 transition-colors hover:bg-[#f3f3f6] md:flex-none"
              >
                Cancelar
              </button>
              <button
                type="submit"
                form="role-form"
                disabled={mutation.isPending || !canEditRole}
                className="pm-label-md inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#142175] px-10 py-2.5 text-white shadow-lg shadow-[#142175]/20 transition-all hover:bg-[#2e3a8c] active:scale-95 disabled:opacity-60 md:flex-none"
              >
                <span className="material-symbols-outlined text-[20px]" aria-hidden="true">
                  save
                </span>
                {mutation.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear rol'}
              </button>
            </div>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
